<template>
  <div>
    <h2 class="mb-4">
      <span v-if="isEditMode">"{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
      <span v-else>{{ $t("legal-document-template-category.new") }}</span>
      <a href="/management/legal-document-template" class="float-right"> <i class="fa fa-chevron-left"></i> {{ $t("legal-document-template-category.library") }}</a>
    </h2>

    <b-row>
      <b-col>
        <h6 class="mt-4">{{ $t("product.text") }}</h6>

        <b-form-tags v-model="form.products" size="lg" class="mb-2" add-on-change no-outer-focus>
          <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
            <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :disabled="disabled || products.length === 0" :options="products">
              <template #first>
                <!-- This is required to prevent bugs with Safari -->
                <option disabled value=""> {{ $t("product.text") }}</option>
              </template>
            </b-form-select>

            <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
              <li v-for="tag in tags" :key="tag" class="list-inline-item">
                <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ tag }}</b-form-tag>
              </li>
            </ul>
            <b-form-text v-else>
              <span class="text-danger">{{ $t("product.required") }}</span>
            </b-form-text>
          </template>
        </b-form-tags>

        <h6 class="mt-2">{{ $t("common.language") }}</h6>
        <multiselect
          v-model="form.language"
          track-by="language"
          :placeholder="$t('common.language-select')"
          :options="languages"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            <img :src="`${option.flag}`" alt="user-image" class="mr-1" height="12" />
            <span class="align-middle">{{ option.title[option.language] }}</span>
          </template>
          <template slot="option" slot-scope="{ option }">
            <img :src="`${option.flag}`" alt="user-image" class="mr-1" height="12" />
            <span class="align-middle">{{ option.title[option.language] }}</span>
          </template>
        </multiselect>

        <h6 class="mt-3">{{ $t("legal-document-template-category.text") }} :</h6>
        <div class="mb-3">
          <filter-select
            id="categoryFilter"
            ref="categoryFilter"
            :placeholder="$t('legal-document-template-category.text')"
            :multiple="false"
            @filter="onCategorySearch"
            :selected="form.category"
            @changed="onCategorySelect"
          />
        </div>

        <sweet-input
          class="mt-3"
          v-model="form.name"
          :isValid="!$v.form.name.$error"
          @input="$v.form.name.$touch()"
          layout="vertical"
          :placeholder="$t('legal-document-template.name')"
          :label="$t('legal-document-template.name')"
        />
        <b-form-checkbox switch v-model="form.isSystemSpecific" :value="true" :unchecked-value="false" class="custom-checkbox mb-3 mt-3" checked>
          Sistem Kullanıcılarına Özel
        </b-form-checkbox>
        <sweet-textarea
          class="mt-3"
          v-model="form.description"
          :isValid="!$v.form.description.$error"
          @input="$v.form.description.$touch()"
          type="text"
          layout="vertical"
          :placeholder="$t('common.description')"
          :label="$t('common.description')"
          :rows="10"
        />

        <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" buttonClass="mt-2">
          {{ $t("common.save") }}
        </sweet-button>
      </b-col>
      <b-col offset="1">
        <h6 class="mt-3">{{ $t("legal-document-template.filters.title") }} :</h6>

        <b-form-checkbox switch disabled v-model="form.filters.organization" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.organization") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.department" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.department") }}
        </b-form-checkbox>

        <b-form-checkbox switch v-model="form.filters.process" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.process") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.datasource" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.datasource") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.dataCategory" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.data-category") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.datasubject" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.datasubject") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.thirdParty" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.third-party") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.receiverGroup" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.receiver-group") }}
        </b-form-checkbox>
        <!-- <b-form-checkbox switch v-model="form.filters.transferingDirection" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.transfering-direction") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.abroad" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.transfering-abroad") }}
        </b-form-checkbox> -->
        <b-form-checkbox switch v-model="form.filters.physicalStorage" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.physical-storage") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.electronicStorage" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.electronic-storage") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.directCollectionMethod" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.direct-collection-method") }}
        </b-form-checkbox>
        <b-form-checkbox switch v-model="form.filters.otherCollectionMethod" :unchecked-value="false" class="custom-checkbox mb-3">
          {{ $t("legal-document-template.filters.other-collection-method") }}
        </b-form-checkbox>
      </b-col>
    </b-row>
    <hr />

    <h3 class="text-center m-5">{{ $t("legal-document-template.text") }}</h3>
    <Editor ref="editor" />
  </div>
</template>

<script>
import Editor from "@c/document-editor/document-editor";
import SweetTextarea from "@c/form/sweet-textarea";
import { maxLength, minLength, required } from "vuelidate/lib/validators";

const state = "managementLegalDocumentTemplate";

export default {
  components: { Editor, SweetTextarea },
  data() {
    return {
      form: {
        description: undefined,
        category: undefined,
        products: undefined,
        filters: { organization: true },
        content: [],
      },
    };
  },
  validations: {
    form: {
      language: {
        required,
      },
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      description: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(4000),
      },
      category: {
        required,
      },
      products: {
        validate() {
          return this.form.products && this.form.products.length > 0;
        },
      },
    },
  },

  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    languages() {
      return this.$store.getters["localization/languages"];
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const id = this.$route.params.id;
      if (id) {
        const { data } = await this.$store.dispatch(`${state}/findById`, id);

        this.form = {
          ...this.form,
          ...JSON.parse(JSON.stringify(data)),
          language: this.languages.find((i) => i.language == data.language),
          name: data.name[this.currentLanguage],
          description: data.description[this.currentLanguage],
        };

        this.$refs.editor.content = data.content;
      }
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        const editorContent = this.$refs.editor.getEditorContent();
        if (!editorContent) {
          this.$handleHttpErrorByStatusCode(error, { customMessage: this.$t("report-template.required") });
          return;
        }

        await this.$store.dispatch(`${state}/save`, { ...this.form, content: editorContent, language: this.form.language.language });
        this.$toast.success(this.$t("common.success"));

        setTimeout(() => {
          this.$router.push(`/management/legal-document-template`);
        }, 1000);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`managementLegalDocumentTemplateCategory/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onCategorySelect(category) {
      this.form = { ...this.form, category };
    },
  },
};
</script>
